import React, { useEffect } from 'react';
import {Outlet, useLocation, useNavigate} from 'react-router-dom';
import {
    AuditOutlined,
    BarChartOutlined, CreditCardOutlined,
    ExclamationCircleOutlined,
    HistoryOutlined,
    InboxOutlined,
    ProfileOutlined,
    UploadOutlined,
    EnvironmentOutlined,
    SafetyOutlined,
} from '@ant-design/icons';
import MainLayout from '../../../layouts/MainLayout';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import { t } from '../../../i18n';
import { NoPermission } from '../../../components/NoPermission';
import { findFirstAvailableMenuItem } from '../../../utils/menuUtils';
import { renderHistoryMenuLabel } from 'components/parcelQueryRange/HistoryMenuTag';

const QualityControlLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // 包裹查询 / POD 审核 / DSP 申诉审核 / 异常 / 下载记录 / 无法妥投明细上传：
    // 纯资源码控制，与后端 @RequireResource 同判据
    // 两码任一即可进菜单：CS_PACKAGE 落全量查询页，CS_PACKAGE_LITE 落轻量查看页，分流见 routes.tsx
    const canSeeParcels = hasResource([ResourceCode.cs.CS_PACKAGE, ResourceCode.cs.CS_PACKAGE_LITE]);
    // 历史包裹查询（PRD R6）：仅完整版包裹查询角色，与后端 getOrderListingHistoryForCustomerService 同判据
    const canSeeParcelHistory = hasResource([ResourceCode.cs.CS_PACKAGE]);
    const canSeePodReview = hasResource([ResourceCode.cs.CS_POD_REVIEW]);
    const canSeeReviewTask = hasResource([ResourceCode.cs.CS_DSP_APPEAL_REVIEW]);
    // POP 审核按资源码控制，与后端 @RequireResource(AppResource.CS_POP_REVIEW) 保持同一判据
    const canSeePopReview = hasResource([ResourceCode.cs.CS_POP_REVIEW]);
    // 舆情 POD 审核按资源码控制，与后端 @RequireResource(AppResource.CS_POD_RISK_REVIEW) 同判据
    const canSeePodRiskReview = hasResource([ResourceCode.cs.CS_POD_RISK_REVIEW]);
    // DSP 申诉汇总（全美只读）：纯资源码控制，与后端 @RequireResource(AppResource.CS_DSP_APPEAL_SUMMARY) 同判据；
    // 不叠加旧角色——该页面为新增能力，无存量角色需要兼容，由权限平台按人分配
    const canSeeDspAppealSummary = hasResource([ResourceCode.cs.CS_DSP_APPEAL_SUMMARY]);
    // 免拍地址抽查：纯资源码控制，不叠加旧角色（新增能力无存量角色需兼容，由权限平台按人分配）；
    // 与后端 @RequireResource(AppResource.CS_EXEMPT_SPOT_CHECK) 同判据
    const canSeeExemptSpotCheck = hasResource([ResourceCode.cs.CS_EXEMPT_SPOT_CHECK]);
    // 门牌免拍终审 / 字典：纯资源码控制，与后端 @RequireResource 同判据
    const canSeePodExemptReview = hasResource([ResourceCode.cs.CS_POD_EXEMPT_REVIEW]);
    const canSeePodExemptDict = hasResource([ResourceCode.cs.CS_POD_EXEMPT_DICT]);
    const canSeeExceptions = hasResource([ResourceCode.cs.CS_EXCEPTION]);
    const canSeeDownload = hasResource([ResourceCode.cs.CS_DOWNLOAD]);
    const canSeeUndeliverableUpload = hasResource([ResourceCode.cs.CS_UNDELIVERABLE_UPLOAD]);

    const menuItems = [
        {
            key: 'parcels',
            label: t('customerservice.parcels.menu'),
            icon: <InboxOutlined />,
            display: canSeeParcels,
            onClick: () => navigate('/qualitycontrol/parcels')
        },
        {
            key: 'parcels/history',
            label: renderHistoryMenuLabel(t('central.parcels.menu.history')),
            icon: <HistoryOutlined />,
            display: canSeeParcelHistory,
            onClick: () => navigate('/qualitycontrol/parcels/history')
        },
        {
            key: 'podReview',
            label: t('qualityControl.menu.podReview'),
            icon: <AuditOutlined />,
            display: canSeePodReview,
            onClick: () => navigate('/qualitycontrol/podReview')
        },
        {
            key: 'reviewTask',
            label: t('qualityControl.menu.reviewTask', 'Appeal Review'),
            icon: <AuditOutlined />,
            display: canSeeReviewTask,
            onClick: () => navigate('/qualitycontrol/reviewTask')
        },
        {
            // 紧随申诉审核：两者同属 DSP 申诉链路，前者审核个案、本页汇总全美结果供质控算罚款
            key: 'dspAppealSummary',
            label: t('qualityControl.menu.dspAppealSummary', 'DSP Appeal Summary'),
            icon: <AuditOutlined />,
            display: canSeeDspAppealSummary,
            onClick: () => navigate('/qualitycontrol/dspAppealSummary')
        },
        {
            // 置于申诉审核之下：PRD 评论指出「POP 审核」与「POD 审核」相邻时路由/名称过于相似，易误点
            key: 'popReview',
            label: t('qualityControl.menu.popReview'),
            icon: <AuditOutlined />,
            display: canSeePopReview,
            onClick: () => navigate('/qualitycontrol/popReview')
        },
        {
            // 紧随 POP 审核：同属审核类工作台，但审的是妥投图片的舆情风险，与 POD 审核链路不同
            key: 'podRiskReview',
            label: t('qualityControl.menu.podRiskReview'),
            icon: <AuditOutlined />,
            display: canSeePodRiskReview,
            onClick: () => navigate('/qualitycontrol/podRiskReview')
        },
        {
            key: 'exceptions',
            label: t('qualityControl.menu.exceptions'),
            icon: <ExclamationCircleOutlined />,
            display: canSeeExceptions,
            onClick: () => navigate('/qualitycontrol/exceptions')
        },
        {
            key: 'penaltyUpload',
            label: t('qualityControl.menu.penaltyUpload'),
            icon: <UploadOutlined />,
            display: hasResource([ResourceCode.cs.CS_QC_UPLOAD]),
            onClick: () => navigate('/qualitycontrol/penaltyUpload')
        },
        {
            key: 'driver-verification',
            label: t('riskControl.driverVerification.title'),
            icon: <SafetyOutlined />,
            display: hasResource([ResourceCode.cs.CS_DRIVER_REVIEW]),
            onClick: () => navigate('/qualitycontrol/driver-verification')
        },
        {
            key: 'undeliverable-upload',
            label: t('qualityControl.menu.undeliverableUpload'),
            display: canSeeUndeliverableUpload,
            icon: <UploadOutlined />,
            onClick: () => navigate('/qualitycontrol/undeliverable-upload')
        },
        {
            key: 'download',
            label: t('central.download.title'),
            display: canSeeDownload,
            icon: <BarChartOutlined/>,
            onClick: () => navigate('/qualitycontrol/download')
        },
        {
            key: 'settlement',
            label: t('qualityControl.menu.settlementReview'),
            icon: <CreditCardOutlined />,
            display: hasResource([ResourceCode.cs.CS_SETTLEMENT_REVIEW]),
            children: [
                {
                    key: 'settlement/adjustment-review',
                    label: t('settlement.adjustmentReview.title'),
                    icon: <ProfileOutlined />,
                    display: hasResource([ResourceCode.cs.CS_SETTLEMENT_PROCESS_REVIEW]),
                    onClick: () => navigate('/qualitycontrol/settlement/adjustment-review'),
                }
            ]
        },
        {
            key: 'pod-exempt',
            label: t('podExempt.menu.qc'),
            display: canSeePodExemptReview,
            icon: <EnvironmentOutlined />,
            onClick: () => navigate('/qualitycontrol/pod-exempt')
        },
        {
            // 紧随门牌免拍终审：终审放行的地址正是抽查的对象，两者是同一条免拍链路的前后环节
            key: 'exemptSpotCheck',
            label: t('qualityControl.menu.exemptSpotCheck', 'Exempt Address Spot Check'),
            icon: <EnvironmentOutlined />,
            display: canSeeExemptSpotCheck,
            onClick: () => navigate('/qualitycontrol/exemptSpotCheck')
        },
        {
            key: 'pod-exempt-dict',
            label: t('podExempt.dict.title'),
            display: canSeePodExemptDict,
            icon: <EnvironmentOutlined />,
            onClick: () => navigate('/qualitycontrol/pod-exempt-dict')
        }
    ];

    useEffect(() => {
        const isRoot = location.pathname === '/qualitycontrol' || location.pathname === '/qualitycontrol/';
        if (!isRoot) return;
        const first = findFirstAvailableMenuItem(menuItems);
        if (first?.onClick) first.onClick();
    }, [location.pathname, menuItems]);

    // 二级菜单一个可点击项都没有（资源码不够）时，视为无权限，兜底展示 NoPermission 而不是空白页
    const hasVisibleMenu = findFirstAvailableMenuItem(menuItems) !== null;

    return (
        <MainLayout
            activeFirstMenu="qualitycontrol"
            secondMenu={menuItems}
        >
            <div className="p-6 h-full flex flex-col">
                {hasVisibleMenu ? <Outlet /> : <NoPermission />}
            </div>
        </MainLayout>
    );
};

export default QualityControlLayout;
